import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, MessageSquare, ChevronDown, LogIn, LogOut, UserPlus, Settings as SettingsIcon } from "lucide-react";
import { useUser } from "../context/UserContext";
import NotificationsCenter from "./NotificationsCenter";

const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
    className={({ isActive }) =>
      `px-3 py-2 text-sm transition-colors ${isActive ? "text-white" : "text-[#a0a0b0] hover:text-white"}`
    }
  >
    {children}
  </NavLink>
);

export const Navbar = () => {
  const { user, signOut, openChat, unreadTotal } = useUser();
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setDropdown(false); }, [location.pathname]);

  const isCreator = user?.role === "creator";

  const goToHIW = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById("how-it-works");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#how-it-works");
      setTimeout(() => {
        const el = document.getElementById("how-it-works");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <header
      data-testid="global-navbar"
      className={`sticky top-0 z-40 transition-all ${
        scrolled ? "backdrop-blur-xl bg-[#06060c]/85 border-b border-[#15152a]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center h-16">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 mr-8">
          <span className="text-[#00ff88] text-2xl leading-none" style={{ textShadow: "0 0 18px rgba(0,255,136,0.5)" }}>◈</span>
          <span className="font-semibold tracking-tight text-white">CHEATCRAFT</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavItem to="/creators">Creators</NavItem>
          <a
            href="/#how-it-works"
            data-testid="nav-how-it-works"
            onClick={goToHIW}
            className="px-3 py-2 text-sm text-[#a0a0b0] hover:text-white transition-colors"
          >
            How It Works
          </a>
          <NavItem to="/faq">FAQ</NavItem>
          {user && <NavItem to="/orders">Orders</NavItem>}
          {user && <NavItem to="/dashboard">Dashboard</NavItem>}
          {user && (isCreator ? <NavItem to="/wallet">Wallet</NavItem> : <NavItem to="/payments">Payments</NavItem>)}
          {user && isCreator && <NavItem to="/settings">Edit Profile</NavItem>}
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          {user && (
            <>
              <button
                data-testid="nav-chat-btn"
                onClick={() => openChat()}
                className="relative p-2 rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14] transition-colors"
                aria-label="Messages"
              >
                <MessageSquare size={18} />
                {unreadTotal > 0 ? (
                  <span
                    data-testid="nav-chat-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#00ff88] text-[#06060c] text-[10px] font-bold flex items-center justify-center"
                  >
                    {unreadTotal > 9 ? "9+" : unreadTotal}
                  </span>
                ) : (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#00ff88]/40" />
                )}
              </button>
              <NotificationsCenter />
            </>
          )}

          {user === null && (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-4 py-2 text-sm border border-[#15152a] bg-[#0a0a14] text-white rounded-lg hover:border-[#1f1f3a] inline-flex items-center gap-1"
              >
                <LogIn size={14} /> Sign in
              </Link>
              <Link
                to="/signup"
                data-testid="nav-signup"
                className="cc-btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1"
              >
                <UserPlus size={14} /> Sign up
              </Link>
            </div>
          )}

          {/* Discord pill — always visible (logged-out AND logged-in) */}
          <a
            href="https://discord.gg/YYsb5qkmX"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-discord-btn"
            aria-label="Join our Discord"
            className="ml-1 w-9 h-9 rounded-full bg-[#00bbff] hover:bg-[#33c9ff] active:bg-[#0099dd] transition-colors inline-flex items-center justify-center shadow-[0_0_0_1px_rgba(0,187,255,0.25),0_4px_14px_rgba(0,187,255,0.35)] hover:scale-105 duration-150"
            title="Join our Discord"
          >
            <svg
              viewBox="0 0 127.14 96.36"
              width="16"
              height="16"
              fill="#ffffff"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
            </svg>
          </a>

          {user && (
            <div className="relative ml-2">
              <button
                data-testid="user-dropdown-btn"
                onClick={() => setDropdown((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#15152a] bg-[#0a0a14] hover:border-[#1f1f3a] transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-[#0d0d1a] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
                  {user.glyph || "◆"}
                </span>
                <div className="hidden sm:flex flex-col items-start leading-none">
                  <span className="text-[11px] text-[#606070] uppercase tracking-widest">{isCreator ? "Creator" : "Company"}</span>
                  <span className="text-sm text-white">{user.display_name}</span>
                </div>
                <ChevronDown size={14} className="text-[#606070]" />
              </button>

              {dropdown && (
                <div
                  data-testid="user-menu"
                  className="absolute right-0 mt-2 w-72 cc-elevated rounded-xl shadow-2xl overflow-hidden z-50"
                >
                  <div className="px-4 py-3 border-b border-[#15152a]">
                    <div className="text-sm text-white truncate">{user.display_name}</div>
                    <div className="text-[11px] text-[#606070] truncate">{user.email}</div>
                  </div>
                  <div className="py-1">
                    <Link to="/dashboard" data-testid="menu-dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14]">
                      Dashboard
                    </Link>
                    {isCreator ? (
                      <Link to="/settings" data-testid="menu-settings" className="flex items-center gap-2 px-4 py-2 text-sm text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14]">
                        <SettingsIcon size={14} /> Edit profile
                      </Link>
                    ) : (
                      <Link to="/payments" data-testid="menu-payments" className="flex items-center gap-2 px-4 py-2 text-sm text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14]">
                        Payments
                      </Link>
                    )}
                  </div>
                  <div className="border-t border-[#15152a]">
                    <button
                      data-testid="menu-signout"
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2.5 text-sm text-[#ffcccc] hover:bg-[#ff4444]/10 inline-flex items-center gap-2"
                    >
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
